// app/javascript/controllers/markdown_editor_controller.js

/**
  This Stimulus controller manages an EasyMDE markdown editor instance with configurable toolbars
  and optional image upload capabilities. It provides three preset configurations:

  - full: Complete editor with image uploads and all features
  - basic: Essential editing features without image handling
  - minimal: Just bold and italic for simple text editing

  Usage in views:

  Full editor with image uploads:
  <%= form.text_area :body,
    data: {
      controller: "markdown-editor",
      markdown_editor_target: "field",
      markdown_editor_model_type_value: form.object.class.name,
      markdown_editor_model_id_value: form.object&.id,
      markdown_editor_upload_url_value: mardkown_image_uploads_path,
      markdown_editor_toolbar_value: "full"
    }
  %>

  Basic editor (no uploads):
  <%= form.text_area :body,
    data: {
      controller: "markdown-editor",
      markdown_editor_target: "field",
      markdown_editor_toolbar_value: "basic"
    }
  %>

  Minimal editor:
  <%= form.text_area :body,
    data: {
      controller: "markdown-editor",
      markdown_editor_target: "field",
      markdown_editor_toolbar_value: "minimal"
    }
  %>
 */

import { Controller } from "@hotwired/stimulus"
import EasyMDE from "easymde"

export default class extends Controller {
  // Define the textarea target that will be transformed into the markdown editor
  static targets = ["field"]

  // Define the values that can be provided via data attributes
  static values = {
    modelType: { type: String, default: null },    // Optional: only needed for full editor with uploads
    modelId: { type: Number, default: null },      // Optional: only needed for full editor with uploads
    uploadUrl: { type: String, default: null },    // Optional: only needed for full editor with uploads
    toolbar: { type: String, default: "basic" }    // Which toolbar configuration to use
  }

  /**
   * Initialize the controller
   * This runs once when the controller is first instantiated
   */
  initialize() {
    // Create a global storage for pending uploads that persists across page refreshes
    // This is necessary because the editor might be reinitialized during form submission
    if (!window.easyMDEPendingUploads) {
      window.easyMDEPendingUploads = new Map()
    }

    // Set up listeners for Turbo's page transition events to handle editor cleanup
    // and reinitialization properly
    document.addEventListener("turbo:render", this.handleTurboRender.bind(this))
    document.addEventListener("turbo:before-render", this.handleBeforeRender.bind(this))
  }

  /**
   * Connect the controller
   * This runs every time the controller is connected to the DOM
   */
  connect() {
    this.initEditor()
  }

  /**
   * Disconnect the controller
   * This runs when the controller is removed from the DOM
   */
  disconnect() {
    this.cleanup()
  }

  /**
   * Handle Turbo render events
   * Reinitializes the editor if the current element is part of the render
   */
  handleTurboRender(event) {
    if (event.target.contains(this.element)) {
      this.initEditor()
    }
  }

  /**
   * Handle cleanup before Turbo renders new content
   */
  handleBeforeRender(event) {
    this.cleanup()
  }

  // Update these class properties to use arrow functions for proper binding
  imageSizeButton = {
    name: "imageSize",
    action: (editor) => this.handleImageSize(editor),
    className: "fa fa-expand",
    title: "Toggle image size"
  }

  imageAlignButton = {
    name: "imageAlign",
    action: (editor) => this.handleImageAlign(editor),
    className: "fa fa-align-center",
    title: "Toggle image alignment"
  }

  customHeadingButton = {
    name: "heading",
    action: (editor) => this.handleHeadingAction(editor),
    className: "fa fa-header",
    title: "Add heading (H2-H6)"
  }

  /**
   * Initialize the EasyMDE editor
   * This creates a new editor instance if one doesn't already exist
   */
  initEditor() {
    if (this.editor) return

    const savedContent = this.fieldTarget.value
    const isFullEditor = this.toolbarValue === 'full'

    const config = {
      element: this.fieldTarget,
      toolbar: this.getToolbarConfig(this.customHeadingButton, this.imageSizeButton, this.imageAlignButton),
      spellChecker: false,
      status: false,
      initialValue: savedContent || '',
      shortcuts: {
        "toggleHeading1": null,
        "toggleHeading2": "Ctrl-H",
        "toggleHeadingSmaller": "Shift-Ctrl-H",
        "toggleHeadingBigger": null
      }
    }

    // Add image upload config for full editor
    if (isFullEditor && this.hasUploadUrlValue) {
      Object.assign(config, {
        uploadImage: true,
        imageAccept: "image/png, image/jpeg, image/jpg",
        previewImagesInEditor: true,
        previewClass: "easy-mde-custom-style",
        imageUploadFunction: this.handleImageUpload.bind(this)
      })
    }

    this.editor = new EasyMDE(config)

    // Listen for changes within the EasyMDE editor
    this.editor.codemirror.on("change", () => {
      this.handleEditorChange();
    });

    // Set up image tracking for full editor
    if (isFullEditor && this.hasUploadUrlValue) {
      const editorId = this.fieldTarget.id || Math.random().toString(36).substr(2, 9)
      this.editor.codemirror.getWrapperElement().dataset.easyMdeId = editorId
      this.restorePendingUploads(editorId)
    }
  }

  /**
   * Clean up the editor instance
   * Saves current content and destroys the editor
   */
  cleanup() {
    if (this.editor) {
      // It's good practice to remove event listeners when cleaning up
      // Note: .off might not remove an anonymous function handler correctly if it's not the exact same reference.
      // For this specific anonymous function, this .off call might not be effective, but the primary goal is achieved by the 'on' handler.
      this.editor.codemirror.off("change", this.handleEditorChange); 
      const content = this.editor.value()
      this.fieldTarget.value = content
      this.editor.toTextArea()
      this.editor = null
    }
  }

  /**
   * Get the toolbar configuration based on the selected preset
   * This determines which buttons appear in the editor's toolbar
   */
  getToolbarConfig(customHeadingButton, imageSizeButton, imageAlignButton) {
    const toolbarPresets = {
      full: [
        "bold", "italic", customHeadingButton, "|",
        "quote", "unordered-list", "ordered-list", "|",
        "link", "image", {
          name: "upload-image",
          action: EasyMDE.drawUploadedImage,
          className: "fa fa-upload", // Changed from "fa fa-image" to "fa fa-upload"
          title: "Upload Image"
        }, imageSizeButton, imageAlignButton, "|",
        "preview", "side-by-side", "fullscreen", "|",
        "guide"
      ],
      basic: [
        "bold", "italic", customHeadingButton, "|",
        "unordered-list", "ordered-list", "|",
        "preview"
      ],
      minimal: [
        "bold", "italic"
      ]
    }

    return toolbarPresets[this.toolbarValue] || toolbarPresets.basic
  }

  /**
   * Handle image upload requests from the editor
   * This method manages both immediate uploads and pending uploads
   */
  handleImageUpload(file, onSuccess, onError) {
    // Prevent image uploads if not using full toolbar
    if (this.toolbarValue !== 'full') {
      onError('Image uploads are not available in this editor configuration')
      return
    }

    // Ensure upload URL is configured
    if (!this.hasUploadUrlValue) {
      onError('Upload URL not configured')
      return
    }

    // Handle uploads for new records (no model ID yet)
    if (!this.canUploadImages) {
      const editorId = this.editor.codemirror.getWrapperElement().dataset.easyMdeId
      const placeholderText = `![Pending upload - ${file.name}](pending)`

      // Store the upload details for later processing
      if (!window.easyMDEPendingUploads.has(editorId)) {
        window.easyMDEPendingUploads.set(editorId, [])
      }

      window.easyMDEPendingUploads.get(editorId).push({
        file,
        onSuccess,
        onError,
        placeholderText
      })

      // Insert a placeholder for the image in the editor
      const cm = this.editor.codemirror
      const doc = cm.getDoc()
      const cursor = doc.getCursor()
      doc.replaceRange(`\n${placeholderText}\n`, cursor)

      console.log("Image will be uploaded after saving the form")
      return
    }

    // Handle immediate uploads when model exists
    this.uploadImage(file)
      .then(data => {
        if (data.success) {
          onSuccess(data.url)
        } else {
          onError(this.formatError(data.error))
        }
      })
      .catch(error => {
        onError(`Upload failed: ${error.message}`)
      })
  }

  /**
   * Process any pending uploads that were stored while waiting for the model to be created
   */
  async restorePendingUploads(editorId) {
    const pendingUploads = window.easyMDEPendingUploads.get(editorId) || []
    if (pendingUploads.length === 0 || !this.canUploadImages) return

    // Process each pending upload sequentially
    for (const { file, placeholderText } of pendingUploads) {
      try {
        const data = await this.uploadImage(file)
        if (data.success) {
          // Replace the placeholder text with the actual image markdown
          const content = this.editor.value()
          const updatedContent = content.replace(
            placeholderText,
            `![${file.name}](${data.url})`
          )
          this.editor.value(updatedContent)

          // Force CodeMirror to refresh the preview
          this.editor.codemirror.refresh()
        }
      } catch (error) {
        console.error('Failed to restore pending upload:', error)
      }
    }

    // Clear processed uploads from storage
    window.easyMDEPendingUploads.delete(editorId)
  }

  /**
   * Handle changes to the modelId value
   * This typically happens after a new record is created
   */
  modelIdValueChanged() {
    if (this.modelIdValue && this.editor) {
      const editorId = this.editor.codemirror.getWrapperElement().dataset.easyMdeId
      this.restorePendingUploads(editorId)
    }
  }

  /**
   * Upload an image to the server
   * Makes an AJAX request to the configured upload URL
   */
  async uploadImage(file) {
    const formData = new FormData()
    formData.append("image", file)
    formData.append("model_type", this.modelTypeValue)
    formData.append("model_id", this.modelIdValue)

    const response = await fetch(this.uploadUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": this.csrfToken
      },
      credentials: 'same-origin'
    })

    return response.json()
  }

  /**
   * Check if we can upload images
   * This requires having a valid model ID and being in full editor mode
   */
  get canUploadImages() {
    return this.toolbarValue === 'full' &&
      this.hasModelIdValue &&
      this.modelIdValue !== null &&
      this.hasUploadUrlValue
  }

  /**
   * Get the CSRF token from the page's meta tags
   * Required for making AJAX requests in Rails
   */
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  /**
   * Format error messages for display
   * Handles both string and array error messages
   */
  formatError(error) {
    return Array.isArray(error) ? error.join(', ') : error
  }

  handleHeadingAction = (editor) => {
    const cm = editor.codemirror
    const cursor = cm.getCursor()
    const line = cm.getLine(cursor.line)
    const tokens = line.trim().split(' ')
    const currentHeading = tokens[0]?.match(/#{1,6}/)

    let newHeading
    if (currentHeading) {
      const level = currentHeading[0].length
      newHeading = level >= 6 ? '' : '#'.repeat(level + 1)
    } else {
      newHeading = '##'
    }

    const lineStart = line.slice(0, tokens[0].length)
    const restOfLine = line.slice(tokens[0].length)

    const newLine = lineStart.replace(/#{1,6}\s*/, '')
    cm.replaceRange(
      `${newHeading}${newHeading ? ' ' : ''}${newLine}${restOfLine}`,
      { line: cursor.line, ch: 0 },
      { line: cursor.line, ch: line.length }
    )
    cm.focus()
  }

  handleImageSize = (editor) => {
    const cm = editor.codemirror
    const doc = cm.getDoc()
    const cursor = doc.getCursor()
    const line = doc.getLine(cursor.line)

    // Look for image markdown syntax with optional Kramdown attributes
    const imgRegex = /!\[([^\]]*)\]\(([^)]+)\)({:.*?})?/
    const match = line.match(imgRegex)

    if (match) {
      console.log("Found image match:", match)  // Debug
      const [fullMatch, alt, url, existingAttrs] = match
      let attrs = {}

      // Parse existing Kramdown attributes if any
      if (existingAttrs) {
        const sizeMatch = existingAttrs.match(/size="([^"]*)"/)
        if (sizeMatch) attrs.size = sizeMatch[1]

        const alignMatch = existingAttrs.match(/align="([^"]*)"/)
        if (alignMatch) attrs.align = alignMatch[1]
      }

      // Cycle through sizes
      attrs.size = attrs.size === 'small' ? 'medium' :
        attrs.size === 'medium' ? undefined : 'small'

      // Build new Kramdown attributes string
      const newAttrs = []
      if (attrs.size) newAttrs.push(`size="${attrs.size}"`)
      if (attrs.align) newAttrs.push(`align="${attrs.align}"`)

      const newAttrStr = newAttrs.length > 0 ? `{:${newAttrs.join(' ')}}` : ''
      const newLine = `![${alt}](${url})${newAttrStr}`

      console.log("New line:", newLine)  // Debug

      const from = { line: cursor.line, ch: 0 }
      const to = { line: cursor.line, ch: line.length }
      doc.replaceRange(newLine, from, to)
    }
  }

  handleImageAlign = (editor) => {
    const cm = editor.codemirror
    const doc = cm.getDoc()
    const cursor = doc.getCursor()
    const line = doc.getLine(cursor.line)

    // Look for image markdown syntax with optional Kramdown attributes
    const imgRegex = /!\[([^\]]*)\]\(([^)]+)\)({:.*?})?/
    const match = line.match(imgRegex)

    if (match) {
      const [fullMatch, alt, url, existingAttrs] = match
      let attrs = {}

      // Parse existing Kramdown attributes if any
      if (existingAttrs) {
        const sizeMatch = existingAttrs.match(/size="([^"]*)"/)
        if (sizeMatch) attrs.size = sizeMatch[1]

        const alignMatch = existingAttrs.match(/align="([^"]*)"/)
        if (alignMatch) attrs.align = alignMatch[1]
      }

      // Cycle through alignments
      attrs.align = attrs.align === 'left' ? 'right' :
        attrs.align === 'right' ? 'center' : 'left'

      // Build new Kramdown attributes string
      const newAttrs = []
      if (attrs.size) newAttrs.push(`size="${attrs.size}"`)
      if (attrs.align) newAttrs.push(`align="${attrs.align}"`)

      const newAttrStr = newAttrs.length > 0 ? `{:${newAttrs.join(' ')}}` : ''
      const newLine = `![${alt}](${url})${newAttrStr}`

      const from = { line: cursor.line, ch: 0 }
      const to = { line: cursor.line, ch: line.length }
      doc.replaceRange(newLine, from, to)
    }
  }

  /**
   * Handles changes from the EasyMDE editor instance.
   * Updates the original textarea's value and dispatches an 'input' event
   * so that other controllers (e.g., autosave) can react to it.
   */
  handleEditorChange() {
    if (this.editor && this.hasFieldTarget) {
      this.fieldTarget.value = this.editor.value();
      // Dispatch an 'input' event on the original textarea
      const event = new Event('input', { bubbles: true, cancelable: true });
      this.fieldTarget.dispatchEvent(event);
    }
  }

  // Helper function for creating image markdown with Kramdown attributes
  createMarkdownImageString(url, alt = '') {
    return `![${alt}](${url}){:align="center"}`
  }
}
